import { RELAY_PFAD } from '../config'

/**
 * Der Draht zwischen Tablet und Fernseher.
 *
 * Der Fernseher zeigt nur an, was ihm gesagt wird. Alle Entscheidungen
 * bleiben auf dem Tablet: welche Aufgabe kommt, wie lange gebraucht wurde,
 * was gespeichert wird. Deshalb kann der Fernseher jederzeit ausfallen oder
 * neu laden, ohne dass etwas verloren geht. Er fragt dann einfach nach dem
 * aktuellen Stand.
 */

export type TvInhalt =
  | { art: 'warten' }
  | { art: 'aufgabe'; text: string; nummer: number; gesamt: number; antworten: number[] | null }
  | { art: 'rueckmeldung'; richtig: boolean; loesung: number; merksatz: string | null; text: string }
  | { art: 'kette'; laenge: number }
  | { art: 'fertig'; gewusst: number; gesamt: number; schnellsteMs: number | null }

type Eingang =
  | { typ: 'code'; code: string }
  | { typ: 'verbunden'; code: string }
  | { typ: 'fehler'; grund: string }
  | { typ: 'tablet-da' }
  | { typ: 'tablet-weg' }
  | { typ: 'tv-weg' }
  | { typ: 'zeigen'; inhalt: TvInhalt }
  | { typ: 'stand-anfordern' }
  | { typ: 'pong' }

export type Verbindungslage = 'verbinde' | 'offen' | 'gekoppelt' | 'getrennt' | 'fehler'

function relayAdresse(): string {
  const protokoll = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protokoll}//${window.location.host}${RELAY_PFAD}`
}

export interface VerbindungsHandler {
  onLage?: (lage: Verbindungslage, grund?: string) => void
  onCode?: (code: string) => void
  onZeigen?: (inhalt: TvInhalt) => void
  onStandAngefordert?: () => void
}

export class Verbindung {
  private sockel: WebSocket | null = null
  private puls: number | null = null
  private absichtlichZu = false
  private readonly handler: VerbindungsHandler

  constructor(handler: VerbindungsHandler) {
    this.handler = handler
  }

  private oeffnen(beimOeffnen: () => void): void {
    this.handler.onLage?.('verbinde')
    try {
      this.sockel = new WebSocket(relayAdresse())
    } catch {
      this.handler.onLage?.('fehler', 'kein-websocket')
      return
    }

    this.sockel.onopen = () => {
      beimOeffnen()
      // Alle 25 Sekunden ein Lebenszeichen. Ohne das schliessen manche
      // Zwischenstationen eine stille Verbindung nach einer Minute.
      this.puls = window.setInterval(() => this.senden({ typ: 'ping' }), 25_000)
    }

    this.sockel.onmessage = (ereignis) => {
      let nachricht: Eingang
      try {
        nachricht = JSON.parse(String(ereignis.data)) as Eingang
      } catch {
        return
      }
      this.verarbeiten(nachricht)
    }

    this.sockel.onclose = () => {
      this.pulsStoppen()
      if (!this.absichtlichZu) this.handler.onLage?.('getrennt')
    }

    this.sockel.onerror = () => {
      this.handler.onLage?.('fehler', 'verbindung')
    }
  }

  private verarbeiten(nachricht: Eingang): void {
    switch (nachricht.typ) {
      case 'code':
        this.handler.onCode?.(nachricht.code)
        this.handler.onLage?.('offen')
        break
      case 'verbunden':
      case 'tablet-da':
        this.handler.onLage?.('gekoppelt')
        break
      case 'tablet-weg':
        this.handler.onLage?.('offen')
        break
      case 'tv-weg':
        this.handler.onLage?.('getrennt', 'tv-weg')
        break
      case 'zeigen':
        this.handler.onZeigen?.(nachricht.inhalt)
        break
      case 'stand-anfordern':
        this.handler.onStandAngefordert?.()
        break
      case 'fehler':
        this.handler.onLage?.('fehler', nachricht.grund)
        break
      default:
        break
    }
  }

  /**
   * Als Fernseher anmelden und einen Kopplungscode anfordern.
   *
   * Der vorherige Code wird mitgeschickt, falls es einen gibt. Steht der Raum
   * noch, macht das Spiel ohne Zutun weiter. Ein Smart-TV laedt schneller neu,
   * als einem lieb ist, und niemand soll deshalb vier Ziffern neu abtippen.
   */
  alsTv(vorherigerCode?: string | null): void {
    this.oeffnen(() =>
      this.senden({ typ: 'tv-anmelden', code: vorherigerCode ?? undefined }),
    )
  }

  /** Als Tablet einem Fernseher beitreten. */
  alsTablet(code: string): void {
    this.oeffnen(() => this.senden({ typ: 'beitreten', code }))
  }

  zeigen(inhalt: TvInhalt): void {
    this.senden({ typ: 'zeigen', inhalt })
  }

  standAnfordern(): void {
    this.senden({ typ: 'stand-anfordern' })
  }

  private senden(nachricht: unknown): void {
    if (this.sockel && this.sockel.readyState === WebSocket.OPEN) {
      this.sockel.send(JSON.stringify(nachricht))
    }
  }

  private pulsStoppen(): void {
    if (this.puls !== null) {
      window.clearInterval(this.puls)
      this.puls = null
    }
  }

  schliessen(): void {
    this.absichtlichZu = true
    this.pulsStoppen()
    this.sockel?.close()
    this.sockel = null
  }
}
